import { OverlayTextAlignment, TextPosition } from "common/components/image-types";
import { TextSize, TextStyle } from "component-utils/text-types";
import { css } from "styled-components";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import { initialsBackground, initialsFontColor } from "../../../lib/theme/theme";
import { styled } from "../../../styled-components";
import Img from "../../smart/img/img";

export function subtitleSize(textSize: TextSize, theme: RuntimeTheme) {
    switch (textSize) {
        case TextSize.Small:
            return theme.gridItem.subtitle.smallFontStyle;
        case TextSize.Large:
            return theme.gridItem.subtitle.largeFontStyle;
    }
    return theme.gridItem.subtitle.fontStyle;
}

export function titleSize(textSize: TextSize, theme: RuntimeTheme) {
    switch (textSize) {
        case TextSize.Small:
            return theme.gridItem.title.smallFontStyle;
        case TextSize.Large:
            return theme.gridItem.title.largeFontStyle;
    }
    return theme.gridItem.title.fontStyle;
}

const noWrap = css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const allCaps = css`
    font-variant: all-small-caps;
    text-transform: lowercase;
`;

export const TileImageWrapper = styled.div<{ height: number; width: number; hasCorners: boolean; isCircle: boolean }>`
    position: relative;
    height: ${p => p.height}px;

    /* Use a large number instead of 50% */
    border-radius: ${p => (p.isCircle ? 1000000 : p.hasCorners ? 6 : 0)}px;

    overflow: hidden;

    & > canvas {
        /* Use a large number instead of 50% */
        border-radius: ${p => (p.isCircle ? 1000000 : p.hasCorners ? 6 : 0)}px;
    }
`;

export const TileItemImageStyle = styled(Img)<{ minDimension: number }>`
    background-color: ${p => p.theme.skeletonColor};
    object-fit: cover;

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    // This ensures that emoji scale up to the minimum dimension
    font-size: ${p => Math.round(0.6 * p.minDimension)}px;
`;

export const TileItemInitials = styled.div<{ hasCorners: boolean }>`
    border-radius: ${p => (p.hasCorners ? 6 : 0)}px;

    background-color: ${p => initialsBackground(p.theme)};
    color: ${p => initialsFontColor(p.theme)};

    display: flex;
    justify-content: center;
    align-items: center;

    padding: 0 20px;

    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

export const InitialsText = styled.div`
    ${p => p.theme.fontStyles.AccentCallout1}
    text-align: center;

    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
`;

function getPositionProps(textPosition: TextPosition, align: OverlayTextAlignment) {
    if (textPosition === TextPosition.Below) return css``;

    switch (align) {
        case OverlayTextAlignment.Center:
            return css`
                text-align: center;
            `;
        case OverlayTextAlignment.TopLeft:
            return css`
                text-align: start;
            `;
        case OverlayTextAlignment.TopRight:
            return css`
                text-align: end;
            `;
        case OverlayTextAlignment.BottomLeft:
            return css`
                text-align: start;
            `;
        case OverlayTextAlignment.BottomRight:
            return css`
                text-align: end;
            `;
    }
}

export type ShadowPosition = "none" | "top" | "bottom" | "full";

function getOverlayProps(shadow: ShadowPosition) {
    switch (shadow) {
        case "none":
            return css``;
        case "full":
            return css`
                background: rgba(0, 0, 0, 0.4);
            `;
        case "top":
            return css`
                background: linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 60%);
            `;
        case "bottom":
            return css`
                background: linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 60%);
            `;
    }
}

interface Props {
    wrapping: boolean;
    textSize: TextSize;
    textStyle: TextStyle;
    textPosition: TextPosition;
    alignment: OverlayTextAlignment;
    hasCorners: boolean;
    padBottom: boolean;
    isCircle: boolean;
    shadow: ShadowPosition;
}

export const TileItemViewStyle = styled.div<Props>`
    position: relative;
    min-width: 0;
    perspective: 600px;
    transform-style: preserve-3d;

    padding: ${p => p.theme.tileItem.padding}px;
    border-radius: ${p => (p.hasCorners ? p.theme.tileItem.borderRadius : 0)}px;

    &:hover {
        cursor: pointer;
        background: ${p => p.theme.tileItem.selectedColor};
    }

    & .tile-title {
        color: ${p => (p.textPosition === TextPosition.Below ? p.theme.fgColorDark : "white")};
        margin: ${p => (p.textPosition === TextPosition.Overlay ? 0 : 4)}px 0 0 0;
        ${p => titleSize(p.textSize, p.theme)}
        ${p => !p.wrapping && noWrap}
        ${p => p.textStyle === TextStyle.AllCaps && allCaps}
        text-align: ${p => (p.isCircle ? `center` : `unset`)};
    }

    & .tile-subtitle {
        color: ${p => (p.textPosition === TextPosition.Below ? p.theme.fgColorMedium : "white")};
        ${p => subtitleSize(p.textSize, p.theme)}
        ${p => !p.wrapping && noWrap}
        text-align: ${p => (p.isCircle ? `center` : `unset`)};
    }

    & .tile-text-container {
        ${p => getPositionProps(p.textPosition, p.alignment)}
        margin-bottom: ${p => (p.padBottom ? 4 : 0)}px;
    }

    & .tile-image-area {
        position: relative;
    }

    & .tile-overlay {
        border-radius: ${p => (p.hasCorners ? 6 : 0)}px;
        ${p => getOverlayProps(p.shadow)}
    }

    & .tile-inner {
        box-shadow: 0 0 1px rgba(255, 255, 255, 0);
        transition: transform 0.2s;

        color: white;
    }
`;
